import { Button, LinearProgress, Stack, Typography } from '@mui/material';
import { t } from 'i18next';
import Grid from '@mui/material/Grid2';
import { InputField } from '../../../../components/v2/input/InputField';
import { useForm } from 'react-hook-form';
import { SwitchField } from '../../../../components/v2/switch/SwitchField';
import { IUpdateProfileParams } from '../../../../api';
import { updateUserMutation } from '../../../../api/users/query';
import notifyHandler from '../../../../utils/notifyHandler';
import { updateUserSchema } from '../../../../components/v2/division/form/schema/accountSettingsSchema';
import { yupResolver } from '@hookform/resolvers/yup';
import { useAuth } from '../../../auth/hooks';
import { useQueryClient } from '@tanstack/react-query';
import QUERIES from '../../../common/queries';
import InputPhoneNumber from 'components/v2/input/InputPhoneNumber';

const MyAccount = () => {
  //#region Properties

  const queryClient = useQueryClient();
  const { user, userIsLoading } = useAuth();

  const { mutateAsync: updateUserAsync, isPending: isUpdatingUser } = updateUserMutation();

  const { control, getValues, handleSubmit } = useForm({
    resolver: yupResolver(updateUserSchema()),
    values: {
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      email: user?.email || '',
      phoneNumber: user?.phoneNumber || '',
      visiblePrices: user?.visiblePrices || false,
    },
    mode: 'onChange',
  });

  //#endregion

  //#region Methods

  function isLoading(): boolean {
    return userIsLoading || isUpdatingUser;
  }

  async function submitAsync(): Promise<void> {
    if (!user || !user.id) {
      return;
    }

    const payload: Partial<IUpdateProfileParams> = {};
    const values = getValues();

    payload.firstName = values.firstName || '';
    payload.lastName = values.lastName || '';
    payload.email = values.email || '';
    payload.phone = values.phoneNumber || '';
    payload.settings = {
      showPrices: values.visiblePrices,
    };

    await updateUserAsync(
      {
        userId: user.id,
        payload,
      },
      {
        onSuccess: () => {
          notifyHandler({ type: 'success', content: t('myAccount.profileUpdatedSuccessfully') });
          queryClient.invalidateQueries({ queryKey: [QUERIES.user] });
        },
        onError: () => {
          notifyHandler({ type: 'error', content: t('myAccount.somethingHappenedWhileUpdatingProfile') });
        },
      }
    );
  }

  //#endregion

  return (
    <form
      noValidate
      onSubmit={handleSubmit(submitAsync, errors => {
        notifyHandler({ type: 'error', content: t('common.messages.someFormFieldsAreInvalidPleaseCheck') });
      })}
    >
      {isLoading() && <LinearProgress style={{ position: 'absolute', top: 0, left: 0, right: 0 }} />}
      <Stack direction={'column'}>
        <Stack direction={'row'} marginBottom={'1.5rem'}>
          <Stack flex={'1 1 auto'}>
            <Typography variant={'h4'} lineHeight={'120%'}>
              {t('myAccount.yourAccountDetails')}
            </Typography>
          </Stack>
          <Stack flex={'0 0 auto'} direction={'row'} gap={'0.5rem'} alignItems={'center'} justifyContent={'center'}>
            <Button type="submit" fullWidth variant="contained" disabled={isLoading()}>
              {t('myAccount.saveChanges')}
            </Button>
          </Stack>
        </Stack>

        <Grid container spacing={2}>
          <Grid size={{ md: 6, xs: 12 }}>
            <InputField
              control={control}
              name="firstName"
              label={t('myAccount.firstName')}
              disabled={isLoading()}
              required
            />
          </Grid>
          <Grid size={{ md: 6, xs: 12 }}>
            <InputField
              control={control}
              name="lastName"
              label={t('myAccount.lastName')}
              disabled={isLoading()}
              required
            />
          </Grid>
          <Grid size={{ md: 6, xs: 12 }}>
            {/* Following the ticket KONEKT-992 https://esofttech.atlassian.net/browse/KONEKT-992
              to make this field read-only
            */}
            <InputField
              control={control}
              name="email"
              label={t('myAccount.email')}
              slotProps={{
                htmlInput: {
                  readOnly: true,
                },
              }}
              required
            />
          </Grid>
          <Grid size={{ md: 6, xs: 12 }}>
            <InputPhoneNumber
              name="phoneNumber"
              label={t('myAccount.phoneNumber')}
              control={control}
              disabled={isLoading()}
            />
          </Grid>
          <Grid size={{ md: 6, xs: 12 }}>
            <SwitchField label="myAccount.seePrices" control={control} disabled={isLoading()} name="visiblePrices" />
          </Grid>
        </Grid>
      </Stack>
    </form>
  );
};

export default MyAccount;
